import { h } from 'preact';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef } from 'preact/hooks';

import { IconVideoPlaylist } from '../../1-foundation/icons/icons';
import Label from '../../2-atoms/label/label';
import VideoCardInline from '../../3-molecules/video-card-inline/video-card-inline';
import OverlayWrapper from '../../3-molecules/overlay-wrapper/overlay-wrapper';
import { allowBodyScroll, preventBodyScroll } from '../../../../utils/body-scroll';
import { satelliteTracker } from '../../../../services/tracking/satellite-tracking-service';
import { VIDEO_PLAYER_STATUS } from '../../../../components/video-player/constants';
import { useStateAsRef } from '../../../hooks/use-state-as-ref';
import { handleCarouselKeyboardNavigation } from '../../../../utils/carousel-utils';
import { createSkyPlayersManager } from '../../../../utils/video-player-utils';

const VIDEO_CAROUSEL_CARD = 'c-video-card-inline';

const VideoPlaylistPopup = ({
  isActive,
  onCancel,
  headerTitle,
  title,
  sectionLabel,
  playlistVideos,
  advOff,
  indexOnPlay,
  onCardClick,
}) => {
  const VIDEO_CARD_MODIFIERS = ['on-popup'];
  const PLAYER_TYPE_ON_VPI_POPUP = 'video_playlist_modale';
  const DEFAULT_POPUP_MUTE_VALUE = false;
  const NEXT_VIDEO_DELAY = 4000;

  const idCurrentlyOnPlay = playlistVideos[indexOnPlay]?.id;
  const htmlId = idCurrentlyOnPlay ? `VPI-player-${idCurrentlyOnPlay}` : '';
  const playlistRef = useRef();
  const popupRef = useRef();
  const skyPlayersManagerOnPopup = useMemo(() => createSkyPlayersManager(), []);

  const [nextVideoTimeoutIDRef, setNextVideoTimeoutID] = useStateAsRef();

  const handleClose = () => {
    satelliteTracker.trackInWeb('generic_interaction', { interaction_name: 'videoplaylist_inline_close' });
    onCancel();
  };

  const scrollToCard = (cardNavigator) => {
    const cardHeight = playlistRef.current.children[0].clientHeight;
    setTimeout(() => {
      playlistRef.current.scrollTo({
        top: cardHeight * cardNavigator,
        behaviour: 'smooth',
      });
    }, 500);
  };

  const scrollOnTop = () => {
    setTimeout(() => {
      popupRef.current.scrollTo({
        top: 0,
        behaviour: 'smooth',
      });
    }, 500);
  };

  const playNextVideo = () => {
    const timeoutID = setTimeout(() => {
      scrollToCard(indexOnPlay + 1);
      onCardClick(indexOnPlay + 1);
    }, NEXT_VIDEO_DELAY);
    setNextVideoTimeoutID(timeoutID);
  };

  const cancelPlayNextVideo = () => {
    clearTimeout(nextVideoTimeoutIDRef.current);
  };

  const handleCardClick = (index) => {
    cancelPlayNextVideo();
    onCardClick(index);
    scrollOnTop();
  };

  const initPlayerListeners = (player) => {
    player.on(VIDEO_PLAYER_STATUS.COMPLETE, () => {
      const isLastVideo = indexOnPlay === playlistVideos.length - 1;
      if (!isLastVideo) {
        playNextVideo();
      }
    });
    player.on(VIDEO_PLAYER_STATUS.REPLAY_CLICK, () => {
      cancelPlayNextVideo();
    });
  };

  useEffect(() => {
    if (isActive) {
      preventBodyScroll();
      satelliteTracker.trackInWeb('videoplaylist_inline_open');
      scrollToCard(indexOnPlay);
    } else {
      allowBodyScroll();
    }
  }, [isActive]);

  useEffect(() => {
    if (isActive && idCurrentlyOnPlay) {
      const player = skyPlayersManagerOnPopup.init().initPlayer(htmlId, {
        id: idCurrentlyOnPlay,
        domain: 'sky',
        autostart: true,
        advOff,
        playertype: PLAYER_TYPE_ON_VPI_POPUP,
        mute: DEFAULT_POPUP_MUTE_VALUE,
      });
      initPlayerListeners(player);
    }
  }, [isActive, idCurrentlyOnPlay]);

  useEffect(() => {
    if (isActive) {
      setTimeout(() => {
        playlistRef.current?.children?.[indexOnPlay || 0].children?.[0]?.focus();
      }, 0);
    }
  }, [isActive]);

  useEffect(() => {
    const unbindKeyboardNavigation = handleCarouselKeyboardNavigation({
      parent: playlistRef.current,
      slideSelector: VIDEO_CAROUSEL_CARD,
      direction: 'vertical',
    });

    return () => {
      unbindKeyboardNavigation();
    };
  }, [isActive, playlistVideos.length]);

  if (!playlistVideos.length) {
    return null;
  }

  return (
    <OverlayWrapper showOverlay={isActive}>
      <div ref={popupRef} className="c-video-playlist-popup" data-state="active">
        <div className="c-video-playlist-popup__header">
          <div className="c-video-playlist-popup__title-and-icon-container">
            <IconVideoPlaylist modifiers={['mix', 'video-playlist', 'xsmall', 'c-light']} />
            <div className="c-video-playlist-popup__header-title">{headerTitle}</div>
          </div>
          <button className="c-cta--close c-cta--on-dark" type="button" onClick={handleClose} arial-label="Chiudi">
            <div>
              <div />
            </div>
          </button>
        </div>
        <div className="o-aspect-ratio o-aspect-ratio--16-9">
          <div className="o-absolute-positioned">
            <div className="c-video-playlist-popup__player" id={htmlId} />
          </div>
        </div>
        <div className="c-video-playlist-popup__section">
          {sectionLabel && <Label modifiers={['article-heading-white']}>{sectionLabel}</Label>}
        </div>
        <h3 className="c-video-playlist-popup__section-title">{title}</h3>
        <ul ref={playlistRef} className="c-video-playlist-popup__cards">
          {playlistVideos.map((video, index) => (
            <li
              key={index}
              className="c-video-playlist-popup__card"
              data-state={index === indexOnPlay ? 'active' : undefined}
            >
              <VideoCardInline
                modifiers={VIDEO_CARD_MODIFIERS}
                onClick={handleCardClick}
                index={index}
                showPosition
                isPlaying={index === indexOnPlay}
                imageSrc={video.imageSrc}
                title={video.title}
                duration={video.duration}
                publishedTime={video.publishedTime}
              />
            </li>
          ))}
        </ul>
      </div>
    </OverlayWrapper>
  );
};

VideoPlaylistPopup.PropTypes = {
  isActive: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onCardClick: PropTypes.func.isRequired,
  headerTitle: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  sectionLabel: PropTypes.string,
  playlistVideos: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      title: PropTypes.string.isRequired,
      imageSrc: PropTypes.string.isRequired,
      duration: PropTypes.string.isRequired,
      publishedTime: PropTypes.string.isRequired,
      isPlaying: PropTypes.bool.isRequired,
    })
  ).isRequired,
  advOff: PropTypes.bool,
  indexOnPlay: PropTypes.number,
};

export default VideoPlaylistPopup;
